import {
  IDisplayPrice,
  IOCAPIProductPromotion,
  IContent,
  IProduct,
  IProductImage
} from '../types';

export const poll = (fn: () => boolean, timeout: number, interval: number) => {
  var endTime = Number(new Date()) + (timeout || 2000);
  interval = interval || 100;

  var checkCondition = function(resolve: any, reject: any) {
    // If the condition is met, we're done!
    var result = fn();
    if (result) {
      resolve(result);
    } else if (Number(new Date()) < endTime) {
      // If the condition isn't met but the timeout hasn't elapsed, go again
      setTimeout(checkCondition, interval, resolve, reject);
    } else {
      // Didn't match and too much time, reject!
      reject(new Error('timed out for ' + fn + ': ' + arguments));
    }
  };

  return new Promise<boolean>(checkCondition);
};

export const pollWindowVariable = async (
  property: string,
  timeout: number = 2000
) => {
  try {
    const w = window as any;
    await poll(
      () => {
        return w[property];
      },
      timeout,
      150
    );

    return w[property];
  } catch (error) {
    return null;
  }
};

export const pollWindowVariables = (properties: string[]) => {
  const promises = properties.map(property => {
    return new Promise(resolve => resolve(pollWindowVariable(property)));
  });

  return promises;
};

export const formatMasterProductPrice = (
  price: number,
  productDisplayPrice: string
) => {
  let displayPrice = JSON.parse(productDisplayPrice),
    promoPrice = price,
    regularPrice = price;

  if (price === null) {
    return null;
  }

  if (typeof displayPrice.promo_price !== 'object') {
    promoPrice = displayPrice.promo_price;
  }

  /**
   * when the regular price is returned as a object,
   * and only the promo price is setup use, promo price as regular
   */
  if (typeof displayPrice.regular === 'object') {
    regularPrice = promoPrice;
  } else {
    regularPrice = displayPrice.regular;
  }

  return {
    ...displayPrice,
    regular: regularPrice,
    promo_price: promoPrice
  };
};

export const formatVariantProductPrice = (
  product: any
): IDisplayPrice | null => {
  const promos: IOCAPIProductPromotion[] = product.product_promotions.filter(
    (promotion: any) => promotion.hasOwnProperty('promotional_price')
  );

  if (!promos.length) {
    return {
      regular: product.price,
      promo_price: null,
      has_same_variant_prices: true,
      percent_off: 0
    };
  }

  const promoPrice = promos.reduce((min, cur) => {
    return cur.promotional_price < min ? cur.promotional_price : min;
  }, promos[0].promotional_price);

  /**
   * Custom product data from OCAPI response.
   * You can add other properties from the OCAPI response when needed
   */
  const formatPromoPrice = {
    regular: product.price,
    promo_price: promoPrice,
    has_same_variant_prices: true,
    percent_off: getPercentOff(product.price, promoPrice)
  };

  return formatPromoPrice;
};

const getPercentOff = (regular: number, promo: number): number => {
  const difference = regular - promo;
  return Math.round(100 - (difference * 100) / regular) / 100;
};

export const getProductIds = (data: any) => {
  const ids = [];

  if (Array.isArray(data)) {
    data.forEach(dataValue => ids.push(...getProductIds(dataValue)));
  } else {
    if (data.hasOwnProperty('productIds')) {
      ids.push(...data.productIds);
    } else if (data.hasOwnProperty('productId')) {
      ids.push(data.productId);
    }

    Object.values(data).forEach(dataValue => {
      if (dataValue instanceof Object) {
        ids.push(...getProductIds(dataValue));
      }
    });
  }

  return ids;
};

export function sendEvent(action: string, additionalParams: any = {}) {
  if (typeof (window as any).tc_events_100 !== 'undefined') {
    // @ts-ignore
    (window as any).tc_events_100(this, 'clickevent', {
      ACTION: action,
      ...additionalParams
    });
  }
}

export const sendWaData = async ({
  siteSection,
  pageName
}: {
  siteSection: string;
  pageName: string;
}) => {
  try {
    const wa_data = await pollWindowVariable('wa_data');
    if (wa_data) {
      setTimeout(() => {
        wa_data.siteSection = siteSection;
        wa_data.pageName = pageName;
      }, 0);
    }
  } catch (error) {
    console.error(error);
  }
};

export const getRedirection = (
  type: string,
  value: string,
  countryCode?: string
) => {
  switch (type) {
    case 'product':
      return `$url('Search-Show','cgid','${value}')$`;

    case 'category':
      return `$url('Product-Show', 'pid', '${value}')$`;

    case 'custom':
      return `${getShopURL()}/${getCountryCodeForUrls(countryCode)}/${value}`;

    default:
      return null;
  }
};

/**
 * @function getProductImage
 * @param  {type} productImages: any {The image_groups property of product OCAPI response}
 * @return {type} {Should return an object with the product box shot}
 */
export const getProductImage = (productImages: any): IProductImage | null => {
  const images = productImages.filter(
    (group: any) => group.view_type === 'large'
  )[0].images;
  const regex = /-\d*.(jpg|jpeg|png)/;
  const boxShotImage = images
    .filter((image: any) => {
      const isBoxShotImage = !regex.test(image.link);
      if (isBoxShotImage) {
        return image;
      }
    })
    .reduce((acc: any, cur: any) => Object.assign(acc, cur), {});

  return boxShotImage.link
    ? {
        name: boxShotImage.title,
        link: boxShotImage.link
      }
    : null;
};

export const getProductURL = (productID: string, countryCode: string) => {
  return `${getShopURL()}/${getCountryCodeForUrls(
    countryCode
  )}/${productID}.html`;
};

export const getShopURL = () => {
  // @ts-ignore
  return window.Constants && window.Constants.SITE_ID.indexOf('uplaypc') !== -1
    ? '/upc'
    : '';
};

export const getCountryCodeForUrls = (countryCode: string) => {
  return countryCode === 'pt' || countryCode === 'la' || countryCode === 'ia'
    ? 'ofertas'
    : countryCode;
};
